import React, { useEffect, useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  AlertCircle,
  ExternalLink,
  Package,
  Search,
  Loader2,
} from 'lucide-react';
import api from '../lib/api';
import { formatNumber } from '../lib/utils';
import logger from '../lib/logger';
import { useNavigate } from 'react-router-dom';

/**
 * Read-only diagnostic drawer for ingredients with stock but no cost.
 * No editing. No override. Operators use this to understand what needs
 * Expense Capture coverage and act upstream.
 */
const MissingCostDrawer = ({ open, onOpenChange, outletId }) => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [expenseCaptureUrl, setExpenseCaptureUrl] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open || !outletId) return;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get(
          `/dashboard/missing-cost-items?outlet_id=${outletId}`
        );
        setItems(res.data?.items || []);
        setExpenseCaptureUrl(res.data?.expense_capture_url || null);
      } catch (e) {
        logger.error('Failed to load missing-cost items:', e);
        setError(
          e?.response?.data?.detail || 'Failed to load missing-cost items.'
        );
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [open, outletId]);

  const handleGoToExpenseCapture = () => {
    if (!expenseCaptureUrl) return;
    window.open(expenseCaptureUrl, '_blank', 'noopener,noreferrer');
  };

  const handleViewIngredient = (name) => {
    onOpenChange(false);
    navigate(`/ingredients?search=${encodeURIComponent(name)}`);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl !max-w-2xl bg-[#09090b] border-l border-[#27272a] overflow-y-auto p-0 flex flex-col"
        data-testid="missing-cost-drawer"
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-[#27272a] space-y-3">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-5 h-5 text-amber-400" />
            </div>
            <div className="flex-1 min-w-0">
              <SheetTitle
                className="text-xl font-bold text-white text-left"
                data-testid="missing-cost-drawer-title"
              >
                Ingredients with stock but missing cost
              </SheetTitle>
              <SheetDescription
                className="text-sm text-zinc-400 mt-1 text-left"
              >
                Read-only diagnostic. Cost data is owned by Expense Capture —
                resolve upstream to populate valuation.
              </SheetDescription>
            </div>
          </div>

          {expenseCaptureUrl && (
            <button
              onClick={handleGoToExpenseCapture}
              className="inline-flex items-center gap-2 self-start px-3 py-2 rounded-md bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 text-sm font-medium border border-emerald-500/30"
              data-testid="missing-cost-go-to-expense-capture"
            >
              <ExternalLink className="w-4 h-4" />
              Go to Expense Capture
            </button>
          )}
        </SheetHeader>

        <div className="flex-1 px-6 py-4 overflow-y-auto">
          {loading && (
            <div
              className="text-center py-16 text-zinc-400"
              data-testid="missing-cost-drawer-loading"
            >
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
              Loading missing-cost ingredients…
            </div>
          )}

          {error && !loading && (
            <div
              className="rounded-md border border-red-500/30 bg-red-500/5 p-3 text-sm text-red-300"
              data-testid="missing-cost-drawer-error"
            >
              {error}
            </div>
          )}

          {!loading && !error && items.length === 0 && (
            <div
              className="text-center py-16 text-zinc-400"
              data-testid="missing-cost-drawer-empty"
            >
              <Package className="w-10 h-10 mx-auto mb-2 text-zinc-500" />
              No ingredients are missing cost.
            </div>
          )}

          {!loading && items.length > 0 && (
            <div className="space-y-2" data-testid="missing-cost-items-list">
              <p className="text-xs uppercase tracking-wider text-zinc-500 mb-3">
                {items.length} ingredient{items.length !== 1 ? 's' : ''} · sorted by stock (highest first)
              </p>
              {items.map((it) => (
                <MissingCostRow
                  key={it.ingredient_id}
                  item={it}
                  onViewIngredient={handleViewIngredient}
                />
              ))}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

// ---------------------------------------------------------------------------

const MissingCostRow = ({ item, onViewIngredient }) => {
  const hasPurchaseHistory = item.mapping_status === 'mapped';
  return (
    <div
      className="rounded-md border border-[#27272a] bg-[#18181b] p-3 flex flex-col gap-2"
      data-testid={`missing-cost-row-${item.ingredient_id}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3
              className="text-sm font-bold text-white truncate"
              data-testid={`missing-cost-name-${item.ingredient_id}`}
            >
              {item.ingredient_name}
            </h3>
            <span
              className={
                'px-2 py-0.5 text-xs font-medium rounded-full border ' +
                (hasPurchaseHistory
                  ? 'bg-sky-500/10 text-sky-300 border-sky-500/30'
                  : 'bg-zinc-500/10 text-zinc-400 border-zinc-500/30')
              }
              data-testid={`missing-cost-mapping-status-${item.ingredient_id}`}
            >
              {hasPurchaseHistory ? 'mapped' : 'never purchased'}
            </span>
          </div>
          <div className="mt-1 text-xs text-zinc-400 flex items-center gap-4 flex-wrap">
            <span data-testid={`missing-cost-soh-${item.ingredient_id}`}>
              Stock:{' '}
              <strong className="text-white">
                {formatNumber(item.soh, 2)} {item.base_unit}
              </strong>
            </span>
            {item.last_purchase_date && (
              <span data-testid={`missing-cost-last-purchase-${item.ingredient_id}`}>
                Last purchase:{' '}
                <strong className="text-zinc-200">
                  {String(item.last_purchase_date).slice(0, 10)}
                </strong>
              </span>
            )}
            {item.last_vendor_name && (
              <span data-testid={`missing-cost-vendor-${item.ingredient_id}`}>
                Vendor:{' '}
                <strong className="text-zinc-200">{item.last_vendor_name}</strong>
              </span>
            )}
          </div>
        </div>
        <button
          onClick={() => onViewIngredient(item.ingredient_name)}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#27272a] hover:bg-[#3f3f46] text-zinc-300 text-xs font-medium"
          data-testid={`missing-cost-view-ingredient-${item.ingredient_id}`}
        >
          <Search className="w-3.5 h-3.5" />
          View
        </button>
      </div>
    </div>
  );
};

export default MissingCostDrawer;
